/**
 * ============================================================================
 * useKbPublicModuleEnabled — Consulta pública (sem auth) que informa se o
 *                            módulo Base de Conhecimento (Wiki) está ativo
 *                            para um tenant a partir do slug.
 * ----------------------------------------------------------------------------
 * Usado pelos layouts públicos da wiki (/kb/:tenantSlug) e pelo portal externo
 * (/extranet/:tenantSlug) para bloquear o acesso quando o módulo for
 * desativado (preparação para gestão de add-on).
 *
 * Fonte: RPC public.kb_public_module_enabled(text).
 * Comportamento: quando o tenant não existir, retorna TRUE, deixando o fluxo
 * padrão de "tenant não encontrado" seguir nas RPCs específicas.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useKbPublicModuleEnabled(tenantSlug: string | undefined) {
  return useQuery({
    queryKey: ["kb-public", "module-enabled", tenantSlug],
    enabled: !!tenantSlug,
    // Cache generoso — a flag muda raramente (evento manual do WL/admin).
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("kb_public_module_enabled", {
        p_tenant_slug: tenantSlug!,
      });
      if (error) {
        // Em caso de falha de rede/RPC, assume habilitado para não bloquear
        // indevidamente páginas que já estavam funcionando.
        return true;
      }
      // A RPC retorna BOOLEAN escalar
      return data !== false;
    },
  });
}
